import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { OffersPayload } from '../../models/offers-form.model';
import { MyOffersService } from '../../services/my-offers.service';
import { Offer, OfferConfirmationInfo } from '../../models/offers.model';
import { User } from '@spartacus/core';

@Component({
    selector: 'app-offer',
    templateUrl: './offer.component.html',
    styleUrl: './offer.component.scss',
    standalone: false
})
export class OfferComponent implements OnInit, OnDestroy {
  @Input({ required: true }) offer!: Offer;
  @Input({ required: true }) baseurl!: string;
  @Input({ required: true }) user!: User;
  @Input() selectedLocation: any;
  @Input() index!: number;
  @Input() total!: number;

  @Output() offerAccepted = new EventEmitter<Offer>();

  @ViewChild('acceptanceSection') acceptanceSection!: ElementRef<HTMLElement>;
  @ViewChild('offerDescriptionStart') offerDescriptionStart!: ElementRef<HTMLElement>;

  private readonly myOffersService = inject(MyOffersService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly cdr = inject(ChangeDetectorRef);

  private toastNotificationTimeout: any;
  private inlineBannerTimeout: any;
  private readonly destroy$ = new Subject<void>();

  showToastNotification = signal<boolean>(false);
  toastMessage = signal<string>('');
  readonly offerConfirmationInfo = signal<OfferConfirmationInfo | null>(null);
  inlineBannerMessage = signal<string | null>(null);
  recalcPillVisible = signal<boolean>(false);

  // Performance data (accepted or not)
  creditAmount: number | null = null; // estimatedCreditAmount (base)
  projectedUpgradeAmount: number | null = null; // upgradeCreditAmount (max)
  totalPurchase: number | null = null;
  scheduledDoses: number | null = null;
  shippedDoses: number | null = null;
  lastUpdated: string | null = null;
  showPerformanceSummary: boolean = false;

  ngOnInit(): void {
    // Load performance data regardless of acceptance
    this.loadPerformanceSummary(this.offer);

    // Acceptance marker (if accepted)
    if (this.offer.confirmedDate && this.offer.offerTraits?.length) {
      const acceptedTrait = this.offer.offerTraits.find((t) => t.accepted);
      if (acceptedTrait) {
        this.offerConfirmationInfo.set({
          confirmationDate: this.offer.confirmedDate,
          acceptedOfferTraitName: acceptedTrait.rebateName || acceptedTrait.name,
        });
      }
    }
  }

  confirmOffer(offerPayload: OffersPayload): void {
    this.myOffersService
      .confirmOffer(offerPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.offerConfirmationInfo.set({
              confirmationDate: res.acceptedDate,
              acceptedOfferTraitName: res.acceptedRebateName,
            });

            this.offer.confirmedDate = res.acceptedDate;
            this.offer.offerTraits.forEach(
              (trait) => (trait.accepted = trait.rebateName === res.acceptedRebateName)
            );

            this.loadPerformanceSummary(this.offer);
            this.cdr.markForCheck?.();

            this.offerAccepted.emit(this.offer);

            this.dataLayerService.addtoDataLayer(
              'offerForm_FormEvent',
              { selected_offer: `${this.offer.name} & ${res.acceptedRebateName}` },
              null,
              null,
              'form_submit'
            );

            this.toggleToastNotification('Congratulations! Offer has been accepted.');
          }
        },
        error: (res: ErrorResponse) => {
          this.dataLayerService.addtoDataLayer(
            'offerForm_FormEvent',
            { error_type: res.errors?.[0]?.type },
            null,
            null,
            'form_error'
          );
        },
      });
  }

  // --- Upgrade Helpers ---
  private acceptTraits(offer: Offer): any[] {
    return (offer.offerTraits || []).filter(
      (t: any) => t.traitType === 'ACCEPT' && t.benefitValue != null
    );
  }

  private acceptedTrait(offer: Offer): any | null {
    return (offer.offerTraits || []).find((t: any) => t.accepted);
  }

  private maxTrait(offer: Offer): any | null {
    const traits = this.acceptTraits(offer);
    if (!traits.length) return null;
    return traits.reduce((best, t) =>
      t.benefitValue > (best?.benefitValue ?? -Infinity) ? t : best
    );
  }

  canShowUpgrade(offer: Offer): boolean {
    const accepted = this.acceptedTrait(offer);
    const max = this.maxTrait(offer);
    if (!accepted || !max || max.benefitValue <= accepted.benefitValue) return false;
    if (!offer.active) return false;
    return true;
  }

  acceptedTraitLabel(offer: Offer): string {
    const t = this.acceptedTrait(offer);
    return t?.rebateName || t?.name || 'current option';
  }

  upgradeTargetLabel(offer: Offer): string {
    const t = this.maxTrait(offer);
    return t?.rebateName || t?.name || 'higher option';
  }

  upgradeOffer(targetTraitId: string): void {
    if (!this.offer || !this.offer.offerTraits) return;

    const offersMap = this.offer.offerTraits
      .filter((trait) => trait.traitType === 'ACCEPT')
      .map((trait) => ({ key: trait.traitId, value: trait.traitId === targetTraitId }));

    this.myOffersService
      .upgradeOffer(this.offer.offerId, { offersMap })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.offerConfirmationInfo.set({
              confirmationDate: res.acceptedDate,
              acceptedOfferTraitName: res.acceptedRebateName,
            });

            this.dataLayerService.addtoDataLayer(
              'offerUpgrade_FormEvent',
              {
                previous_offer: this.acceptedTraitLabel(this.offer),
                upgraded_offer: res.acceptedRebateName,
              },
              null,
              null,
              'form_submit'
            );

            this.offer.offerTraits.forEach((trait) => {
              if (trait.traitType === 'ACCEPT') {
                trait.accepted = trait.traitId === targetTraitId;
              }
            });

            this.loadPerformanceSummary(this.offer);
            this.showInlineUpgradeBanner(
              'Congratulations! Your offer has been upgraded.|Your projected credit will be recalculated and updated here within a few days.'
            );
            this.toggleToastNotification('Congratulations! Offer has been upgraded.');
          }
        },
        error: (err: ErrorResponse) => {
          this.dataLayerService.addtoDataLayer(
            'offerUpgrade_FormEvent',
            { error_type: err.errors?.[0]?.type || 'unknown' },
            null,
            null,
            'form_error'
          );
        },
      });
  }

  private loadPerformanceSummary(offer: Offer): void {
    const opd: any = offer.offerPerformanceData;
    const isAccepted = !!offer.confirmedDate || this.offerConfirmationInfo() !== null;

    const estRaw = (opd?.estimatedCreditAmount ?? '').toString().trim();
    const upgRaw = (opd?.upgradeCreditAmount ?? '').toString().trim();
    const est = Number.isFinite(parseFloat(estRaw)) ? parseFloat(estRaw) : 0;
    const upg = Number.isFinite(parseFloat(upgRaw)) ? parseFloat(upgRaw) : 0;

    const scheduled = opd?.scheduledDoses ?? 0;
    const shipped = opd?.shippedDoses ?? 0;
    const totalPur = Number.isFinite(parseFloat(opd?.totalPurchase ?? ''))
      ? parseFloat(opd.totalPurchase)
      : 0;
    const lastUpd = opd?.lastUpdated ?? '';

    if (isAccepted) {
      if (est > 0) {
        this.creditAmount = est;
        this.projectedUpgradeAmount = upg > 0 ? upg : null;
        this.scheduledDoses = scheduled;
        this.shippedDoses = shipped;
        this.totalPurchase = totalPur;
        this.lastUpdated = lastUpd;

        this.showPerformanceSummary = true;
      } else {
        this.creditAmount = null;
        this.projectedUpgradeAmount = null;
        this.scheduledDoses = null;
        this.shippedDoses = null;
        this.totalPurchase = null;
        this.lastUpdated = null;
        this.showPerformanceSummary = false;
      }
    } else {
      if (upg > 0) {
        this.creditAmount = null;
        this.projectedUpgradeAmount = upg;
        this.scheduledDoses = null;
        this.shippedDoses = null;
        this.totalPurchase = null;
        this.lastUpdated = lastUpd || '';
        this.showPerformanceSummary = true;
      } else {
        this.creditAmount = null;
        this.projectedUpgradeAmount = null;
        this.scheduledDoses = null;
        this.shippedDoses = null;
        this.totalPurchase = null;
        this.lastUpdated = null;
        this.showPerformanceSummary = false;
      }
    }
  }

  private baseTrait(offer: Offer): any | null {
    const traits = (offer.offerTraits || [])
      .filter((t: any) => t.traitType === 'ACCEPT' && t.benefitValue != null)
      .sort((a: any, b: any) => a.benefitValue - b.benefitValue);
    return traits.length ? traits[0] : null;
  }

  baseTraitLabel(offer: Offer): string | null {
    const t = this.baseTrait(offer);
    return t ? t.rebateName || t.name || 'base option' : null;
  }

  jumpToOfferDescription(): void {
    this.dataLayerService.addtoDataLayer(
      'offerNudge_Click',
      {
        action: 'review_offer',
        offerName: this.offer?.name,
        basePotential: this.creditAmount,
        maxPotential: this.projectedUpgradeAmount,
      },
      null,
      null,
      'cta_click'
    );

    const el = this.offerDescriptionStart?.nativeElement;
    if (el) {
      el.setAttribute('tabindex', '-1');
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => el.focus({ preventScroll: true }), 400);
    }
  }

  jumpToAcceptance(): void {
    this.dataLayerService.addtoDataLayer(
      'offerNudge_Click',
      {
        action: 'review_and_enroll',
        offerName: this.offer?.name,
        basePotential: this.creditAmount,
        maxPotential: this.projectedUpgradeAmount,
      },
      null,
      null,
      'cta_click'
    );

    this.acceptanceSection?.nativeElement?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  closeToastNotification(): void {
    this.showToastNotification.set(false);
    clearTimeout(this.toastNotificationTimeout);
  }

  private toggleToastNotification(message: string): void {
    this.toastMessage.set(message);
    this.showToastNotification.set(true);
    clearTimeout(this.toastNotificationTimeout);
    this.toastNotificationTimeout = setTimeout(() => {
      this.showToastNotification.set(false);
    }, 5000);
  }

  private showInlineUpgradeBanner(msg: string, durationMs = 5000): void {
    this.inlineBannerMessage.set(msg);
    this.recalcPillVisible.set(true);

    clearTimeout(this.inlineBannerTimeout);
    this.inlineBannerTimeout = setTimeout(() => {
      this.inlineBannerMessage.set(null);
      this.recalcPillVisible.set(false);
    }, durationMs);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.toastNotificationTimeout);
    clearTimeout(this.inlineBannerTimeout);
  }
}
